import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ModalContent } from "./content";

import { GET_VULN_ADDITIONAL_INFO } from "../additional-info/queries";
import type { IVulnRowAttr } from "../types";
import { formatVulnerabilitiesTreatment } from "../utils";
import { Logger } from "utils/logger";
import { showNotification } from "utils/notifications";

interface IVulnerabilityModalProps {
  readonly cvssVersion: string;
  readonly findingId: string;
  readonly groupName: string;
  readonly vulnerabilityId: string;
  readonly onActionCompleted?: () => void;
}

const VulnerabilityModal = ({
  cvssVersion,
  findingId,
  groupName,
  vulnerabilityId,
  onActionCompleted,
}: IVulnerabilityModalProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { data, refetch } = useQuery(GET_VULN_ADDITIONAL_INFO, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        showNotification("error", t("groupAlerts.errorTextsad"));
        Logger.warning(
          "An error occurred loading the vulnerability info",
          error,
        );
      });
    },
    variables: { canRetrieveHacker: false, vulnId: vulnerabilityId },
  });

  const currentRow = useMemo((): IVulnRowAttr | undefined => {
    const formattedVulns = formatVulnerabilitiesTreatment({
      organizationsGroups: undefined,
      vulnerabilities:
        data === undefined
          ? []
          : [
              {
                ...data.vulnerability,
                where:
                  data.vulnerability.vulnerabilityType === "lines" &&
                  data.vulnerability.rootNickname !== null &&
                  data.vulnerability.rootNickname !== "" &&
                  !data.vulnerability.where.startsWith(
                    `${data.vulnerability.rootNickname}/`,
                  )
                    ? `${data.vulnerability.rootNickname}/${data.vulnerability.where}`
                    : data.vulnerability.where,
              } as unknown as IVulnRowAttr,
            ],
    });

    return formattedVulns.length > 0 ? formattedVulns[0] : undefined;
  }, [data]);

  const onCloseVulnModal = useCallback((): void => {
    const url = pathname.split("/").slice(0, -1).join("/");
    navigate(url, { replace: true });
  }, [navigate, pathname]);

  return currentRow === undefined ? null : (
    <ModalContent
      closeModal={onCloseVulnModal}
      currentRow={currentRow}
      cvssVersion={cvssVersion}
      findingId={findingId}
      groupName={groupName}
      isFindingReleased={true}
      isModalOpen={true}
      onActionCompleted={onActionCompleted}
      refetchData={refetch}
    />
  );
};

export { VulnerabilityModal };
